import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import "./ArcadeDataPopup.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faCoins, faTrophy, faUsers } from "@fortawesome/free-solid-svg-icons";
import PopUpPanel from "../../components/PopUpPanel/PopUpPanel";
import { CampaignContext } from "../../contexts/CampaignContext";
import OneStreamOverview from "../Streams/OneStreamOverview";
import { getTimezoneOptions, parseTimezone } from "../../helpers/TimeZones";
import axios from "../../api/axios";
import { getArcadeEntriesRoute } from "../../api/routes";
import CollapsibleHeadedSection from "../CollapsibleHeadedSection/CollapsibleHeadedSection";
import { convertMSToHMS, convertSecondsToHMS, getDefaultTimezone, getShortDateNumericStringForFilenames, getShortDateTimeString, getShortTimeString, getTzLongDateString, getTzShortDateString } from "../../helpers/Dates";
import { IconJsxer } from "../../helpers/IconHelper";
import LottieIcon from "../common/LottieIcon";
import XLSX from 'xlsx';
import { alphaTypePrimer, numberTypePrimer, sortListOn } from "../../helpers/ListFunctions";

const ArcadeDataPopup = (props) => {
    const [campaignContext] = useContext(CampaignContext);
    const streams_ref = useRef([...campaignContext.campaignData.streams]);

    const arcadeDataFetched_ref = useRef({});
    const [arcadeDataFetched, setArcadeDataFetched] = useState({});
    const arcadeData_ref = useRef({});
    const [arcadeData, setArcadeData] = useState({});

    const getArcadeDataForStreamID = (streamID, featureId = null, force = false) => {
        if (!arcadeDataFetched[streamID] || force) {
            axios.get(getArcadeEntriesRoute, {
                params: {
                    streamID: streamID,
                    featureID: featureId
                }
            }).then(response => {
                // Sort the response data in reverse order based on entry.activationTime
                response.data.sort((a, b) => {
                    return b.activationTime - a.activationTime;
                });
                arcadeData_ref.current[streamID] = response.data;
                arcadeDataFetched_ref.current[streamID] = true;
                setArcadeDataFetched((oldVals) => {
                    return ({
                        ...oldVals,
                        [streamID]: true
                    });
                });
                setArcadeData({
                    ...arcadeData_ref.current
                });
                console.log('Got arcade data:', response.data);
            }).catch(err => {
                console.log(err);
                arcadeDataFetched_ref.current[streamID] = true;
                setArcadeDataFetched((oldVals) => {
                    return ({
                        ...oldVals,
                        [streamID]: true
                    });
                });
            });
        }
    }
    /* useEffect(() => {
        // Sort streams in reverse date order based on stream.startTime
        let streams = streams_ref.current;
        streams.sort((a, b) => {
            return b.startTime - a.startTime;
        });
        console.log('Streams (sorted):', streams);
        streams.forEach(stream => {
            getArcadeDataForStreamID(stream._id);
        });
        getArcadeDataForStreamID('preview_test');
    }, []); */

    const [selectedFeatureFilters, setSelectedFeatureFilters] = useState(props.featureId ? [String(props.featureId)] : []);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setSelectedFeatureFilters(props.featureId ? [String(props.featureId)] : []);
    }, [props.featureId]);

    const checkAllFetched = (streams) => {
        let allFetched = true;
        console.log('Checking all fetched:', streams, arcadeDataFetched_ref.current);
        streams.forEach(stream => {
            if (!arcadeDataFetched_ref.current[stream._id]) {
                allFetched = false;
            }
        });
        return allFetched;
    }

    const fetchData = (streamOverride = null) => {
        if (!busy) {
            setBusy(true);

            arcadeData_ref.current = {};
            arcadeDataFetched_ref.current = {};
            setArcadeDataFetched({});
            setArcadeData({});

            const loc_selectedStream = streamOverride ?? props.streamId ?? 'all';

            if (loc_selectedStream === 'all') {
                setTimeout(() => {
                    let streams = streams_ref.current;
                    streams.sort((a, b) => {
                        return b.startTime - a.startTime;
                    });
                    console.log('Streams (sorted):', streams);
                    streams.forEach(stream => {
                        getArcadeDataForStreamID(stream._id, null, true);
                    });
                    getArcadeDataForStreamID('preview_test', null, true);

                    const haveWeFetchedAll = () => {
                        const allFetched = checkAllFetched(streams);
                        if (allFetched) {
                            setBusy(false);
                        } else {
                            setTimeout(() => {
                                haveWeFetchedAll();
                            }, 100);
                        }
                    }
                    setTimeout(() => {
                        haveWeFetchedAll();
                    }, 1000);
                }, 100);
            } else {
                console.log('Getting data for stream:', loc_selectedStream);
                getArcadeDataForStreamID(loc_selectedStream, null, true);

                const streamDataFetchedCheck = () => {
                    console.log('Checking if all fetched:', loc_selectedStream, arcadeDataFetched_ref.current);
                    const allFetched = arcadeDataFetched_ref.current[loc_selectedStream];
                    if (allFetched) {
                        setBusy(false);
                    } else {
                        setTimeout(() => {
                            streamDataFetchedCheck();
                        }, 100);
                    }
                }
                setTimeout(() => {
                    streamDataFetchedCheck();
                }, 1000);
            }
        }
    }

    const featureFilterList = campaignContext.campaignData.features.reduce((acc, feature) => {
        if (!feature.feature.isTwitchArcade) {
            return acc;
        }
        const featureId = String(feature.feature._id);
        if (acc.some((entry) => entry.id === featureId)) {
            return acc;
        }
        acc.push({
            id: featureId,
            label: feature.feature.featureName,
            iconKey: feature.configKey || feature.varKey || feature.feature.varKey || feature.feature.featureType || null,
        });
        return acc;
    }, []);

    const toggleFeatureFilter = (featureId) => {
        setSelectedFeatureFilters((oldVals) => {
            const normalizedFeatureId = String(featureId);
            if (oldVals.includes(normalizedFeatureId)) {
                return oldVals.filter((value) => value !== normalizedFeatureId);
            }
            return [...oldVals, normalizedFeatureId];
        });
    };

    const clearFeatureFilters = () => {
        setSelectedFeatureFilters([]);
    };

    useEffect(() => {
        fetchData(props.streamId ?? 'all');
        // This is intentionally a mount/open-time fetch for the selected defaults.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.streamId]);

    const getFeatureMeta = (entry) => {
        const normalizeId = (value) => String(value ?? '');
        const campaignFeature = campaignContext.campaignData.features.find((feature) => {
            return normalizeId(feature._id) === normalizeId(entry.campaignFeatureId)
                || normalizeId(feature.feature?._id) === normalizeId(entry.featureID);
        });
        const storedFeature = campaignFeature?.feature || null;
        const iconKey = campaignFeature?.configKey || campaignFeature?.varKey || storedFeature?.varKey || storedFeature?.featureType || null;
        const baseLabel = entry.campaignFeatureName || storedFeature?.featureName || 'Feature';
        const familyLabel = storedFeature?.featureName && storedFeature?.featureName !== entry.campaignFeatureName ? storedFeature.featureName : null;

        return {
            featureId: normalizeId(entry.featureID),
            iconKey,
            baseLabel,
            familyLabel,
        };
    }

    const filteredEntriesForStream = (streamId) => {
        const sourceEntries = arcadeData[streamId] || [];
        if (selectedFeatureFilters.length === 0) {
            return sourceEntries;
        }
        return sourceEntries.filter((entry) => selectedFeatureFilters.includes(String(entry.featureID)));
    };

    const matchesSelectedFeatures = (entry) => {
        if (selectedFeatureFilters.length === 0) {
            return true;
        }
        return selectedFeatureFilters.includes(String(entry.featureID));
    };

    const capitalizeFirstLetter = (string) => {
        if (typeof string !== 'string') {
            return string;
        }
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    const createXlsxData = (entry, timezoneData) => {
        const workBook = XLSX.utils.book_new();

        const campaignSheetData = [];
        campaignSheetData.push(['Campaign Name', campaignContext.campaignData.campaignName]);
        campaignSheetData.push(['Client', campaignContext.campaignData.client]);
        campaignSheetData.push(['Region', capitalizeFirstLetter(campaignContext.campaignData.region)]);
        campaignSheetData.push(['Owner', campaignContext.campaignData.campaignOwnerName]);
        campaignSheetData.push(['Campaign Created', getShortDateTimeString(campaignContext.campaignData.dateCreated)]);
        campaignSheetData.push(['Salesforce No.', campaignContext.campaignData.sfNumber]);
        campaignSheetData.push(['Data Exported', getShortDateTimeString(Date.now())]);
        const campaignSheet = XLSX.utils.aoa_to_sheet(campaignSheetData);
        const max_widths_1 = [campaignSheetData.reduce((w, r) => Math.max(w, String(r[0]).length), 10), campaignSheetData.reduce((w, r) => Math.max(w, String(r[1]).length), 10)];
        campaignSheet['!cols'] = [{ wch: max_widths_1[0] }, { wch: max_widths_1[1] }];
        console.log('Campaign Sheet:', campaignSheet);

        workBook.SheetNames.push('Campaign Data');
        workBook.Sheets['Campaign Data'] = campaignSheet;

        const featureLabel = campaignContext.campaignData.features.find(f => f.feature._id === entry.featureID)?.feature.featureName ?? null;
        let labelToStore = '';
        if (entry.campaignFeatureName) {
            labelToStore = entry.campaignFeatureName;
            if (featureLabel) {
                labelToStore += ` (${featureLabel})`;
            }
        }
        if (!entry.campaignFeatureName && featureLabel) {
            labelToStore = featureLabel;
        }

        const activationSheetData = [];
        if (labelToStore && labelToStore !== '') {
            activationSheetData.push(['Feature', labelToStore]);
        }
        activationSheetData.push(['Activation Time', getTzLongDateString(entry.activationTime, timezoneData.offset)]);
        activationSheetData.push(['(local time)', getShortDateTimeString(entry.activationTime, false)]);
        activationSheetData.push(['Deactivation Time', getTzLongDateString(entry.deActivationTime, timezoneData.offset)]);
        activationSheetData.push(['(local time)', getShortDateTimeString(entry.deActivationTime, false)]);
        activationSheetData.push(['Timezone', timezoneData.label]);
        activationSheetData.push(['', '']);
        activationSheetData.push(['Prize Attempts', entry.attempts]);
        activationSheetData.push(['Prizes Won', entry.prizesWon]);
        activationSheetData.push(['', '']);
        activationSheetData.push(['Game Settings', '']);
        Object.keys(entry.gameSettings).forEach(key => {
            activationSheetData.push([entry.gameSettings[key].label, capitalizeFirstLetter(entry.gameSettings[key].value)]);
        });
        const activationSheet = XLSX.utils.aoa_to_sheet(activationSheetData);
        const max_widths_2 = [activationSheetData.reduce((w, r) => Math.max(w, String(r[0]).length), 10), activationSheetData.reduce((w, r) => Math.max(w, String(r[1]).length), 10)];
        activationSheet['!cols'] = [{ wch: max_widths_2[0] }, { wch: max_widths_2[1] }];
        console.log('Activation Sheet:', activationSheet);

        workBook.SheetNames.push('Activation Data');
        workBook.Sheets['Activation Data'] = activationSheet;

        const prizesSheetData = [];
        prizesSheetData.push(['Name', 'Description', 'Target', 'Quantity']);
        entry.prizes.forEach(prize => {
            prizesSheetData.push([prize.name, prize.description, prize.targetDescription, prize.quantity]);
        });
        const prizesSheet = XLSX.utils.aoa_to_sheet(prizesSheetData);
        const max_widths_3 = [prizesSheetData.reduce((w, r) => Math.max(w, String(r[0]).length), 10), prizesSheetData.reduce((w, r) => Math.max(w, String(r[1]).length), 10), prizesSheetData.reduce((w, r) => Math.max(w, String(r[2]).length), 10), prizesSheetData.reduce((w, r) => Math.max(w, String(r[3]).length), 10)];
        prizesSheet['!cols'] = [{ wch: max_widths_3[0] }, { wch: max_widths_3[1] }, { wch: max_widths_3[2] }, { wch: max_widths_3[3] }];
        console.log('Prizes Sheet:', prizesSheet);

        workBook.SheetNames.push('Prizes');
        workBook.Sheets['Prizes'] = prizesSheet;

        const entriesSheetData = [];
        entriesSheetData.push(['Twitch Handle', 'Channel', 'Time', 'Entry Count']);
        entry.entries.forEach(entry => {
            entriesSheetData.push([entry.twitchHandle, entry.channel, getShortDateTimeString(entry.entryTime, false), entry.entryCount]);
        });
        const entriesSheet = XLSX.utils.aoa_to_sheet(entriesSheetData);
        const max_widths_4 = [entriesSheetData.reduce((w, r) => Math.max(w, String(r[0]).length), 10), entriesSheetData.reduce((w, r) => Math.max(w, String(r[1]).length), 10), entriesSheetData.reduce((w, r) => Math.max(w, String(r[2]).length), 10), entriesSheetData.reduce((w, r) => Math.max(w, String(r[3]).length), 10)];
        entriesSheet['!cols'] = [{ wch: max_widths_4[0] }, { wch: max_widths_4[1] }, { wch: max_widths_4[2] }, { wch: max_widths_4[3] }];
        console.log('Entries Sheet:', entriesSheet);

        workBook.SheetNames.push('Opt Ins');
        workBook.Sheets['Opt Ins'] = entriesSheet;

        const winnersSheetData = [];
        winnersSheetData.push(['Twitch Handle', 'Channel', 'Time', 'Win Data', 'Attempts']);
        entry.winners.forEach(entry => {
            winnersSheetData.push([entry.twitchHandle, entry.channel, getShortDateTimeString(entry.time, false), entry.winData, entry.attempts]);
        });
        const winnersSheet = XLSX.utils.aoa_to_sheet(winnersSheetData);
        const max_widths_5 = [winnersSheetData.reduce((w, r) => Math.max(w, String(r[0]).length), 10), winnersSheetData.reduce((w, r) => Math.max(w, String(r[1]).length), 10), winnersSheetData.reduce((w, r) => Math.max(w, String(r[2]).length), 10), winnersSheetData.reduce((w, r) => Math.max(w, String(r[3]).length), 10), winnersSheetData.reduce((w, r) => Math.max(w, String(r[4]).length), 10)];
        winnersSheet['!cols'] = [{ wch: max_widths_5[0] }, { wch: max_widths_5[1] }, { wch: max_widths_5[2] }, { wch: max_widths_5[3] }, { wch: max_widths_5[4] }];
        console.log('Winners Sheet:', winnersSheet);

        workBook.SheetNames.push('Winners');
        workBook.Sheets['Winners'] = winnersSheet;

        const streamMarkersSheetData = [];
        streamMarkersSheetData.push(['Twitch Handle', 'Channel', 'Time', 'Description']);
        entry.streamMarkers.forEach(entry => {
            const tData = convertSecondsToHMS(entry.time, true);
            streamMarkersSheetData.push([entry.twitchHandle, entry.channel, `${tData.h} hrs ${tData.m} mins ${tData.s} secs (${entry.time} seconds)`, entry.description]);
        });
        const streamMarkersSheet = XLSX.utils.aoa_to_sheet(streamMarkersSheetData);
        const max_widths_7 = [streamMarkersSheetData.reduce((w, r) => Math.max(w, String(r[0]).length), 10), streamMarkersSheetData.reduce((w, r) => Math.max(w, String(r[1]).length), 10), streamMarkersSheetData.reduce((w, r) => Math.max(w, String(r[2]).length), 10), streamMarkersSheetData.reduce((w, r) => Math.max(w, String(r[3]).length), 10)];
        streamMarkersSheet['!cols'] = [{ wch: max_widths_7[0] }, { wch: max_widths_7[1] }, { wch: max_widths_7[2] }, { wch: max_widths_7[3] }];
        console.log('Stream Markers Sheet:', streamMarkersSheet);

        workBook.SheetNames.push('Stream Markers');
        workBook.Sheets['Stream Markers'] = streamMarkersSheet;

        const losersSheetData = [];
        losersSheetData.push(['Twitch Handle', 'Channel', 'Time']);
        entry.losers.forEach(entry => {
            losersSheetData.push([entry.twitchHandle, entry.channel, getShortDateTimeString(entry.time, false)]);
        });
        const losersSheet = XLSX.utils.aoa_to_sheet(losersSheetData);
        const max_widths_6 = [losersSheetData.reduce((w, r) => Math.max(w, String(r[0]).length), 10), losersSheetData.reduce((w, r) => Math.max(w, String(r[1]).length), 10), losersSheetData.reduce((w, r) => Math.max(w, String(r[2]).length), 10)];
        losersSheet['!cols'] = [{ wch: max_widths_6[0] }, { wch: max_widths_6[1] }, { wch: max_widths_6[2] }];
        console.log('Losers Sheet:', losersSheet);

        workBook.SheetNames.push('Losers');
        workBook.Sheets['Losers'] = losersSheet;

        return workBook;
    }

    const downloadXlsxData = (entry, timezoneData, index) => {
        const workBook = createXlsxData(entry, timezoneData);
        XLSX.writeFile(workBook, 'ArcadeData_' + (campaignContext.campaignData.campaignName.replace(/[ ]/g, '-')) + '_' + getShortDateNumericStringForFilenames(entry.activationTime) + '_' + (index + 1) + '.xlsx');
    }

    const SORT_TYPES = {
        HANDLE: 'twitchHandle',
        CHANNEL: 'channel',
        ENTRY_TIME: 'entryTime',
        ENTRY_COUNT: 'entryCount'
    }

    const WINNER_GROUP_TIME_WINDOW_MS = 60 * 1000;

    const isBackupWinner = (winner) => /^\s*\[BACKUP WINNER\]/i.test(String(winner?.winData || ''));

    const getWinnerToken = (winner) => {
        const winData = String(winner?.winData || '').trim();
        const tokenMatch = winData.match(/#[A-Z0-9-]+/i);
        return tokenMatch ? tokenMatch[0].toUpperCase() : winData.toUpperCase();
    };

    const canGroupWinnerWithGroup = (winner, group) => {
        const winnerTime = Number(winner?.time || winner?.entryTime || 0);
        return group.token === getWinnerToken(winner)
            && Math.abs(group.anchorTime - winnerTime) <= WINNER_GROUP_TIME_WINDOW_MS;
    };

    const buildWinnerGroups = (winners) => {
        const groups = [];

        winners.forEach((winner, index) => {
            const winnerTime = Number(winner?.time || winner?.entryTime || 0);
            const winnerToken = getWinnerToken(winner);
            const backupWinner = isBackupWinner(winner);

            let matchingGroup = null;
            for (let groupIndex = groups.length - 1; groupIndex >= 0; groupIndex--) {
                if (!canGroupWinnerWithGroup(winner, groups[groupIndex])) {
                    continue;
                }

                if (!backupWinner && groups[groupIndex].primaryWinner) {
                    continue;
                }

                matchingGroup = groups[groupIndex];
                break;
            }

            if (!matchingGroup) {
                matchingGroup = {
                    id: `${winnerToken || 'winner'}-${winnerTime || index}-${index}`,
                    token: winnerToken,
                    anchorTime: winnerTime,
                    primaryWinner: null,
                    backups: [],
                };
                groups.push(matchingGroup);
            }

            if (backupWinner) {
                matchingGroup.backups.push(winner);
            } else {
                matchingGroup.primaryWinner = winner;
                matchingGroup.anchorTime = winnerTime;
            }
        });

        return groups;
    };

    const getPrimaryWinnerCount = (winners) => buildWinnerGroups(winners).filter((group) => Boolean(group.primaryWinner)).length;

    const getRoundTimeSummary = (entry) => {
        const durationMs = Math.max(0, Number(entry?.deActivationTime || 0) - Number(entry?.activationTime || 0));
        const durationHms = convertMSToHMS(durationMs);

        if (durationHms.h > 0) {
            return `${durationHms.h}h ${durationHms.m_str}m ${durationHms.s_str}s`;
        }

        if (Number(durationHms.m_str) > 0) {
            return `${Number(durationHms.m_str)}m ${durationHms.s_str}s`;
        }

        return `${durationHms.s_str}s`;
    };

    const groupActivationsByDate = (entries, timezoneData) => {
        const groupedEntries = [];

        entries.forEach((entry) => {
            const dateKey = timezoneData
                ? getTzShortDateString(entry.activationTime, timezoneData.offset)
                : getShortDateTimeString(entry.activationTime, false, ' ').split(' ')[0];
            const dateLabel = timezoneData
                ? getTzLongDateString(entry.activationTime, timezoneData.offset)
                : new Date(entry.activationTime).toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                });

            const existingGroup = groupedEntries[groupedEntries.length - 1];
            if (!existingGroup || existingGroup.key !== dateKey) {
                groupedEntries.push({
                    key: dateKey,
                    label: dateLabel,
                    entries: [entry],
                });
                return;
            }

            existingGroup.entries.push(entry);
        });

        return groupedEntries;
    };

    const getUserEntriesJsxBlock = (entries, timezoneData, extraColumns = [], sortFunction = null, currentSort = null, sortListReverse = null, displaySettings = null) => {
        const normalizedExtraColumns = Array.isArray(extraColumns) ? extraColumns.filter((column) => column?.prop) : [];
        return (
            <div className="fl-column no-gap tight">
                <div className="grid sticky">
                    {shouldDisplay(displaySettings, 'results.twitchHandle') &&
                        <div className="header label" onClick={sortFunction ? (event) => {sortFunction(event, SORT_TYPES.HANDLE)} : null}>{getLabel(displaySettings, 'results.twitchHandle',  'Twitch Handle')} {currentSort === SORT_TYPES.HANDLE ? (IconJsxer.GetIcon(sortListReverse ? IconJsxer.ICONS.chevronUp : IconJsxer.ICONS.chevronDown, IconJsxer.ICON_STYLES.roundFormControl)) : ''}</div>
                    }
                    {shouldDisplay(displaySettings, 'results.channel') &&
                        <div className="header label" onClick={sortFunction ? (event) => {sortFunction(event, SORT_TYPES.CHANNEL)} : null}>{getLabel(displaySettings, 'results.channel', 'Channel')} {currentSort === SORT_TYPES.CHANNEL ? (IconJsxer.GetIcon(sortListReverse ? IconJsxer.ICONS.chevronUp : IconJsxer.ICONS.chevronDown, IconJsxer.ICON_STYLES.roundFormControl)) : ''}</div>
                    }
                    {shouldDisplay(displaySettings, 'results.entryTime') &&
                        <div className="header label" onClick={sortFunction ? (event) => {sortFunction(event, SORT_TYPES.ENTRY_TIME)} : null}>{getLabel(displaySettings, 'results.entryTime', 'Time')} {currentSort === SORT_TYPES.ENTRY_TIME ? (IconJsxer.GetIcon(sortListReverse ? IconJsxer.ICONS.chevronUp : IconJsxer.ICONS.chevronDown, IconJsxer.ICON_STYLES.roundFormControl)) : ''}</div>
                    }
                    {normalizedExtraColumns.map((column) => {
                        if (!shouldDisplay(displaySettings, 'results.' + column.prop)) {
                            return null;
                        }

                        return (
                            <div
                                className={`header label${column.align === 'center' ? ' centered' : column.align === 'right' ? ' right' : ''}`}
                                key={'header_' + column.prop}
                                onClick={sortFunction && column.sortType ? (event) => {sortFunction(event, column.sortType)} : null}
                            >
                                {column.heading || ''} {currentSort === column.sortType ? (IconJsxer.GetIcon(sortListReverse ? IconJsxer.ICONS.chevronUp : IconJsxer.ICONS.chevronDown, IconJsxer.ICON_STYLES.roundFormControl)) : ''}
                            </div>
                        );
                    })}
                </div>
                    {entries.map((entry, i) => {
                        const startTimeHms = timezoneData ? convertMSToHMS(entry.entryTime || entry.time, false, timezoneData.offset) : null;
                        const altBgClass = i % 2 === 0 ? ' alt-bg' : '';
                        return (
                            <div className={"grid" + altBgClass} key={'entry_' + i}>
                                {entry.includeChannelLinks !== false &&
                                    <>
                                        {shouldDisplay(displaySettings, 'results.twitchHandle') &&
                                            <div className={"data"}><a href={'https://www.twitch.tv/' + entry.twitchHandle} target="_blank">{entry.twitchHandle}</a></div>
                                        }
                                        {shouldDisplay(displaySettings, 'results.channel') &&
                                            <div className={"data"}><a href={'https://www.twitch.tv/' + entry.channel} target="_blank">{entry.channel}</a></div>
                                        }
                                    </>
                                }
                                {entry.includeChannelLinks === false &&
                                    <>
                                        {shouldDisplay(displaySettings, 'results.twitchHandle') &&
                                            <div className={"data"}>{entry.twitchHandle}</div>
                                        }
                                        {shouldDisplay(displaySettings, 'results.channel') &&
                                            <div className={"data"}>{entry.channel}</div>
                                        }
                                    </>
                                }
                                {shouldDisplay(displaySettings, 'results.entryTime') &&
                                    <div className={"data fl-row"}>
                                        {timezoneData &&
                                            <>
                                                <div className='fl-row shrink'>
                                                    {`${startTimeHms.h_str}:${startTimeHms.m_str}`}
                                                </div>
                                            </>
                                        }

                                        <div className='fl-row shrink light-text local-time-list'>
                                            {getShortTimeString(entry.entryTime || entry.time, false)} (Local Time)
                                        </div>
                                    </div>
                                }
                                {normalizedExtraColumns.map((column) => {
                                    if (!shouldDisplay(displaySettings, 'results.' + column.prop)) {
                                        return null;
                                    }

                                    return (
                                        <div
                                            className={`data${column.align === 'center' ? ' centered' : column.align === 'right' ? ' right' : ''}`}
                                            key={'data_' + column.prop + '_' + i}
                                        >
                                            {entry[column.prop] ?? ''}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                {entries.length === 0 &&
                    <div className="grid">
                        <div className="data" colSpan="5">No Data Recorded</div>
                    </div>
                }
            </div>
        );
    }

    const OptInsList = (props) => {
        const listSliceLength = 25;
        const [sortedOptInList, setSortedOptInList] = useState(props.entries);
        const [optInList, setOptInList] = useState(props.entries.slice(0, listSliceLength));

        const [sortListReverse, setSortListReverse] = useState(false);
        const [currentSort, setCurrentSort] = useState(SORT_TYPES.ENTRY_TIME);

        const sortList = (event, type) => {
            event.preventDefault();
    
            let reverse = !sortListReverse;
            setSortListReverse(reverse);
    
            let sortedList = sortedOptInList.concat();
    
            switch (type){
                case SORT_TYPES.HANDLE:
                    sortListOn(sortedList, 'twitchHandle', reverse, alphaTypePrimer);
                    break;
                case SORT_TYPES.CHANNEL:
                    sortListOn(sortedList, 'channel', reverse, alphaTypePrimer);
                    break;
                case SORT_TYPES.ENTRY_TIME:
                    sortListOn(sortedList, 'entryTime', reverse, numberTypePrimer);
                    break;
                case SORT_TYPES.ENTRY_COUNT:
                    sortListOn(sortedList, 'entryCount', reverse, numberTypePrimer);
                    break;
                default:
                    break;
            }
    
            setCurrentSort(type)
            setSortedOptInList(sortedList);
            setOptInList(sortedList.slice(0, optInList.length));
        }

        const showMore = () => {
            const currentLength = optInList.length;
            const newLength = currentLength + listSliceLength;
            setOptInList(sortedOptInList.slice(0, newLength));
        }

        return (
            <div className="opt-in-scroll-container">
                <div className="fl-column">
                    {getUserEntriesJsxBlock(optInList, props.timezoneData, [{ heading: 'Entries', prop: 'entryCount', sortType: SORT_TYPES.ENTRY_COUNT, align: 'center' }], sortList, currentSort, sortListReverse)}
                    {optInList.length < props.entries.length &&
                        <div className="fl-column no-gap">
                            <div className="bold-text light-text under-list">Showing {optInList.length} of {props.entries.length} entries</div>
                            <div className="fl-row">
                                <div className="shrink">
                                    <button className="standard-button tight" onClick={showMore}>Show More</button>
                                </div>
                                <div className="grow"></div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        );
    }

    const WinnersList = (props) => {
        const winnerGroups = buildWinnerGroups(props.entries);
        const [expandedGroups, setExpandedGroups] = useState(() => {
            const defaultExpanded = {};
            winnerGroups.forEach((group) => {
                if (group.backups.length > 0 && !group.primaryWinner) {
                    defaultExpanded[group.id] = true;
                }
            });
            return defaultExpanded;
        });

        useEffect(() => {
            setExpandedGroups((oldVals) => {
                const nextVals = {};
                winnerGroups.forEach((group) => {
                    if (oldVals[group.id] !== undefined) {
                        nextVals[group.id] = oldVals[group.id];
                    } else if (group.backups.length > 0 && !group.primaryWinner) {
                        nextVals[group.id] = true;
                    }
                });
                return nextVals;
            });
        }, [props.entries]);

        const toggleGroup = (groupId) => {
            setExpandedGroups((oldVals) => ({
                ...oldVals,
                [groupId]: !oldVals[groupId],
            }));
        };

        return (
            <div className="fl-column no-gap tight winners-list">
                <div className="grid winners-grid sticky">
                    {shouldDisplay(props.displaySettings, 'results.twitchHandle') &&
                        <div className="header label">{getLabel(props.displaySettings, 'results.twitchHandle', 'Twitch Handle')}</div>
                    }
                    {shouldDisplay(props.displaySettings, 'results.channel') &&
                        <div className="header label">{getLabel(props.displaySettings, 'results.channel', 'Channel')}</div>
                    }
                    {shouldDisplay(props.displaySettings, 'results.entryTime') &&
                        <div className="header label">{getLabel(props.displaySettings, 'results.entryTime', 'Time')}</div>
                    }
                    {shouldDisplay(props.displaySettings, 'results.winData') &&
                        <div className="header label">{getLabel(props.displaySettings, 'results.winData', 'Win Data')}</div>
                    }
                    {shouldDisplay(props.displaySettings, 'results.attempts') &&
                        <div className="header label centered">{getLabel(props.displaySettings, 'results.attempts', getLabel(props.displaySettings, 'attempts', 'Attempts'))}</div>
                    }
                    <div className="header label right winner-backup-toggle-header">Backup Winners</div>
                </div>

                {winnerGroups.map((group, groupIndex) => {
                    const leadWinner = group.primaryWinner || group.backups[0];
                    const startTimeHms = props.timezoneData ? convertMSToHMS(leadWinner?.time || leadWinner?.entryTime, false, props.timezoneData.offset) : null;
                    const hasBackups = group.backups.length > 0;
                    const isExpanded = Boolean(expandedGroups[group.id]);
                    const leadIsBackup = isBackupWinner(leadWinner);

                    return (
                        <div className={`winner-group${groupIndex % 2 === 0 ? ' alt-bg' : ''}${!leadIsBackup ? ' has-primary-winner' : ' backup-only-group'}`} key={group.id}>
                            <div className={`grid winners-grid winner-group-row${hasBackups ? ' is-expandable' : ''}`} onClick={hasBackups ? () => toggleGroup(group.id) : null}>
                                {shouldDisplay(props.displaySettings, 'results.twitchHandle') &&
                                    <div className="data winner-handle-cell">
                                        {leadWinner?.includeChannelLinks !== false
                                            ? <a href={'https://www.twitch.tv/' + leadWinner?.twitchHandle} target="_blank">{leadWinner?.twitchHandle}</a>
                                            : leadWinner?.twitchHandle
                                        }
                                    </div>
                                }
                                {shouldDisplay(props.displaySettings, 'results.channel') &&
                                    <div className="data">
                                        {leadWinner?.includeChannelLinks !== false
                                            ? <a href={'https://www.twitch.tv/' + leadWinner?.channel} target="_blank">{leadWinner?.channel}</a>
                                            : leadWinner?.channel
                                        }
                                    </div>
                                }
                                {shouldDisplay(props.displaySettings, 'results.entryTime') &&
                                    <div className="data fl-row">
                                        {props.timezoneData && startTimeHms &&
                                            <div className='fl-row shrink'>
                                                {`${startTimeHms.h_str}:${startTimeHms.m_str}`}
                                            </div>
                                        }
                                        <div className='fl-row shrink light-text local-time-list'>
                                            {getShortTimeString(leadWinner?.time || leadWinner?.entryTime, false)} (Local Time)
                                        </div>
                                    </div>
                                }
                                {shouldDisplay(props.displaySettings, 'results.winData') &&
                                    <div className="data winner-win-data-cell">
                                        <div className="winner-win-data-row">
                                            <span className="winner-win-data-text">{leadWinner?.winData ?? ''}</span>
                                            <span className={`winner-status-badge${leadIsBackup ? ' is-backup' : ' is-primary'}`}>
                                                {leadIsBackup ? 'Backup winner only' : 'Primary winner'}
                                            </span>
                                        </div>
                                    </div>
                                }
                                {shouldDisplay(props.displaySettings, 'results.attempts') &&
                                    <div className="data centered">{leadWinner?.attempts ?? ''}</div>
                                }
                                <div className="data right winner-backup-toggle-cell">
                                    {hasBackups &&
                                        <span className="winner-group-toggle-copy">
                                            {group.backups.length} backup winner{group.backups.length === 1 ? '' : 's'} {IconJsxer.GetIcon(isExpanded ? IconJsxer.ICONS.chevronUp : IconJsxer.ICONS.chevronDown, IconJsxer.ICON_STYLES.roundFormControl)}
                                        </span>
                                    }
                                </div>
                            </div>

                            {hasBackups && isExpanded &&
                                <div className="winner-group-backups">
                                    {group.backups.map((backupWinner, backupIndex) => {
                                        const backupTimeHms = props.timezoneData ? convertMSToHMS(backupWinner?.time || backupWinner?.entryTime, false, props.timezoneData.offset) : null;
                                        return (
                                            <div className={`grid winners-grid winner-group-backup-row${backupIndex % 2 === 0 ? ' alt-backup-row' : ''}`} key={`${group.id}_backup_${backupIndex}`}>
                                                {shouldDisplay(props.displaySettings, 'results.twitchHandle') &&
                                                    <div className="data winner-handle-cell">
                                                        {backupWinner?.includeChannelLinks !== false
                                                            ? <a href={'https://www.twitch.tv/' + backupWinner?.twitchHandle} target="_blank">{backupWinner?.twitchHandle}</a>
                                                            : backupWinner?.twitchHandle
                                                        }
                                                    </div>
                                                }
                                                {shouldDisplay(props.displaySettings, 'results.channel') &&
                                                    <div className="data">
                                                        {backupWinner?.includeChannelLinks !== false
                                                            ? <a href={'https://www.twitch.tv/' + backupWinner?.channel} target="_blank">{backupWinner?.channel}</a>
                                                            : backupWinner?.channel
                                                        }
                                                    </div>
                                                }
                                                {shouldDisplay(props.displaySettings, 'results.entryTime') &&
                                                    <div className="data fl-row">
                                                        {props.timezoneData && backupTimeHms &&
                                                            <div className='fl-row shrink'>
                                                                {`${backupTimeHms.h_str}:${backupTimeHms.m_str}`}
                                                            </div>
                                                        }
                                                        <div className='fl-row shrink light-text local-time-list'>
                                                            {getShortTimeString(backupWinner?.time || backupWinner?.entryTime, false)} (Local Time)
                                                        </div>
                                                    </div>
                                                }
                                                {shouldDisplay(props.displaySettings, 'results.winData') &&
                                                    <div className="data winner-win-data-cell">
                                                        <div className="winner-win-data-row">
                                                            <span className="winner-win-data-text">{backupWinner?.winData ?? ''}</span>
                                                            <span className="winner-status-badge is-backup">Backup winner</span>
                                                        </div>
                                                    </div>
                                                }
                                                {shouldDisplay(props.displaySettings, 'results.attempts') &&
                                                    <div className="data centered">{backupWinner?.attempts ?? ''}</div>
                                                }
                                                <div className="data right winner-backup-toggle-cell"></div>
                                            </div>
                                        );
                                    })}
                                </div>
                            }
                        </div>
                    );
                })}

                {winnerGroups.length === 0 &&
                    <div className="grid winners-grid">
                        <div className="data">No Data Recorded</div>
                    </div>
                }
            </div>
        );
    }

    const shouldDisplay = (displaySettings, dataPath) => {
        if (displaySettings?.displaySettings) {
            displaySettings = displaySettings.displaySettings;
        }
        if (!displaySettings || !displaySettings.ignore) {
            return true;
        }
        // displaySettings.ignore is an array of paths to ignore
        return displaySettings.ignore.indexOf(dataPath) === -1;
    }
    const getLabel = (displaySettings, dataPath, label) => {
        if (displaySettings?.displaySettings) {
            displaySettings = displaySettings.displaySettings;
        }
        if (!displaySettings || !displaySettings.relabel) {
            return label;
        }
        // displaySettings.relabel is an array of objects {path: pathString, label: "new label"}
        if (Array.isArray(displaySettings.relabel)) {
            for (let i = 0; i < displaySettings.relabel.length; i++) {
                if (displaySettings.relabel[i].path === dataPath) {
                    return displaySettings.relabel[i].label;
                }
            }
        }
        // Not found - use the original label
        return label;
    }

    const getActivationEntryId = (entry, keyPrefix, index) => {
        return `${keyPrefix}_${entry.activationTime}_${entry.featureID}_${index}`;
    };

    const getArcadeDataEntryJsxBlock = (entry, i, timezoneData = null, activationSectionProps = null) => {
        if (!matchesSelectedFeatures(entry)) {
            return null;
        }
        const startTimeHms = timezoneData ? convertMSToHMS(entry.activationTime, false, timezoneData.offset) : null;
        const endTimeHms = timezoneData ? convertMSToHMS(entry.deActivationTime, false, timezoneData.offset) : null;
        const featureMeta = getFeatureMeta(entry);
        const primaryWinnerCount = getPrimaryWinnerCount(entry.winners);
        const detailsHeaderMetrics = (
            <div className="arcade-details-header-metrics">
                <div className="arcade-details-header-metric">
                    <FontAwesomeIcon icon={faUsers} />
                    <span>{entry.entries.length} opt-ins</span>
                </div>
                <div className="arcade-details-header-metric">
                    <FontAwesomeIcon icon={faTrophy} />
                    <span>{primaryWinnerCount} winners</span>
                </div>
                <div className="arcade-details-header-metric">
                    <FontAwesomeIcon icon={faCoins} />
                    <span>{entry.attempts || 0} credits</span>
                </div>
                <div className="arcade-details-header-metric">
                    <FontAwesomeIcon icon={faClock} />
                    <span>{getRoundTimeSummary(entry)}</span>
                </div>
            </div>
        );

        return (
            <div className={`arcade-data-box${activationSectionProps?.open ? ' is-active-activation' : ''}`} key={'arcade_data_' + i}>
                <div className='fl-row list-view-content'>
                    <div className="fl-row grow">
                        <div className="light-text bold-text">Activation #{i + 1}</div>
                        <div className="arcade-feature-pill">
                            <div className="arcade-feature-pill-icon">
                                <LottieIcon varKey={featureMeta.iconKey} size={22} />
                            </div>
                            <div className="light-text bold-text">
                                {featureMeta.baseLabel}
                                {featureMeta.familyLabel && <> ({featureMeta.familyLabel})</>}
                            </div>
                        </div>
                        {timezoneData &&
                            <>
                                <div className='fl-row shrink arcade-data-heading-row-block'>
                                    {IconJsxer.GetIcon(IconJsxer.ICONS.scheduleNav, IconJsxer.ICON_STYLES.listIcon)}
                                    {getTzLongDateString(entry.activationTime, timezoneData.offset)}
                                </div>

                                <div className='fl-row shrink arcade-data-heading-row-block'>
                                    {IconJsxer.GetIcon(IconJsxer.ICONS.clock, IconJsxer.ICON_STYLES.listIcon)}
                                    {`${startTimeHms.h_str}:${startTimeHms.m_str} - ${endTimeHms.h_str}:${endTimeHms.m_str} (${timezoneData.label.replace('(', '').replace(')', '')})`}
                                </div>
                            </>
                        }

                        <div className='fl-row shrink arcade-data-heading-row-block light-text sl-local-time'>
                            {IconJsxer.GetIcon(IconJsxer.ICONS.clock, IconJsxer.ICON_STYLES.listIcon)}
                            {getShortDateTimeString(entry.activationTime, false, ', ')} - {getShortTimeString(entry.deActivationTime, false)} (Local Time)
                        </div>

                        <div className="grow"></div>

                        <div className='fl-row shrink arcade-data-heading-row-block bold-text'>
                            {IconJsxer.GetIcon(IconJsxer.ICONS.arcadeAttemps, IconJsxer.ICON_STYLES.listIcon)}
                            {entry.attempts}
                        </div>

                        <div className='fl-row shrink arcade-data-heading-row-block bold-text'>
                            {IconJsxer.GetIcon(IconJsxer.ICONS.arcadePrizes, IconJsxer.ICON_STYLES.listIcon)}
                            {entry.prizesWon}
                        </div>
                        <div className="fl-row shrink edit-icon" onClick={(e) => {e.preventDefault(); e.stopPropagation(); downloadXlsxData(entry, timezoneData, i)}}>
                            {IconJsxer.GetIcon(IconJsxer.ICONS.fileDownload, IconJsxer.ICON_STYLES.listIcon)}
                        </div>

                    </div>
                </div>

                <CollapsibleHeadedSection
                    id={activationSectionProps?.id}
                    title="Details"
                    open={activationSectionProps?.open ?? false}
                    onCollapseToggle={activationSectionProps?.onCollapseToggle ?? null}
                    extraHeaderContentRight={detailsHeaderMetrics}
                >
                    <div className="arcade-data-box no-box indented">
                        <CollapsibleHeadedSection
                            title="Game Setup"
                        >
                            <div className="fl-column no-gap tight">
                                <div className="grid game-setup">
                                    {Object.keys(entry.gameSettings).map((key, i) => {
                                        if (key === 'displaySettings' || key === 'relabel') {
                                            return null;
                                        }
                                        return (
                                            <Fragment key={'d_' + i}>
                                                <div className="data light-text">{entry.gameSettings[key].label}</div>
                                                <div className="data">{String(entry.gameSettings[key].value)}</div>
                                            </Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        </CollapsibleHeadedSection>
                        {shouldDisplay(entry.gameSettings, 'prizes') &&
                            <CollapsibleHeadedSection
                                title={"Prizes (" + entry.prizes.length + ")"}
                            >
                                <div className="fl-column no-gap tight">
                                    <div className="grid prizes">
                                        {shouldDisplay(entry.gameSettings, 'prizes.name') &&
                                            <div className="header label">{getLabel(entry.gameSettings, 'prizes.name', 'Name')}</div>
                                        }
                                        {shouldDisplay(entry.gameSettings, 'prizes.description') &&
                                            <div className="header label">{getLabel(entry.gameSettings, 'prizes.description', 'Description')}</div>
                                        }
                                        {shouldDisplay(entry.gameSettings, 'prizes.targetDescription') &&
                                            <div className="header label">{getLabel(entry.gameSettings, 'prizes.targetDescription', 'Target')}</div>
                                        }
                                        {shouldDisplay(entry.gameSettings, 'prizes.quantity') &&
                                            <div className="header label centered">{getLabel(entry.gameSettings, 'prizes.quantity', 'Quantity')}</div>
                                        }
                                    </div>
                                    {entry.prizes.map((prize, i) => {
                                        const altBgClass = i % 2 === 0 ? ' alt-bg' : '';
                                        return (
                                            <div className={"grid prizes" + altBgClass} key={'p_' + i}>
                                                {shouldDisplay(entry.gameSettings, 'prizes.name') &&
                                                    <div className="data">{prize.name}</div>
                                                }
                                                {shouldDisplay(entry.gameSettings, 'prizes.description') &&
                                                    <div className="data">{prize.description}</div>
                                                }
                                                {shouldDisplay(entry.gameSettings, 'prizes.targetDescription') &&
                                                    <div className="data">{prize.targetDescription}</div>
                                                }
                                                {shouldDisplay(entry.gameSettings, 'prizes.quantity') &&
                                                    <div className="data centered">{prize.quantity}</div>
                                                }
                                            </div>
                                        );
                                    })}
                                    {entry.prizes.length === 0 &&
                                        <div className="grid prizes">
                                            <div className="data" colSpan="4">No Prizes</div>
                                        </div>
                                    }
                                </div>
                            </CollapsibleHeadedSection>
                        }
                        <CollapsibleHeadedSection
                            title={"Opt Ins (" + entry.entries.length + ")"}
                        >
                            {/*getUserEntriesJsxBlock(entry.entries, timezoneData, "Entries", "entryCount")*/}
                            <OptInsList entries={entry.entries} timezoneData={timezoneData} />
                        </CollapsibleHeadedSection>
                        <CollapsibleHeadedSection
                            title={"Winners (" + primaryWinnerCount + ")"}
                        >
                            <WinnersList entries={entry.winners} timezoneData={timezoneData} displaySettings={entry.gameSettings} />
                        </CollapsibleHeadedSection>
                        {shouldDisplay(entry.gameSettings, 'streamMarkers') &&
                            <CollapsibleHeadedSection
                                title={"Stream Markers (" + entry.streamMarkers.length + ")"}
                            >
                                <div className="fl-column no-gap tight">
                                    <div className="grid stream-markers">
                                        {shouldDisplay(entry.gameSettings, 'streamMarkers.twitchHandle') &&
                                            <div className="header label">{getLabel(entry.gameSettings, 'streamMarkers.twitchHandle', 'Twitch Handle')}</div>
                                        }
                                        {shouldDisplay(entry.gameSettings, 'streamMarkers.channel') &&
                                            <div className="header label">{getLabel(entry.gameSettings, 'streamMarkers.channel', 'Channel')}</div>
                                        }
                                        {shouldDisplay(entry.gameSettings, 'streamMarkers.time') &&
                                            <div className="header label">{getLabel(entry.gameSettings, 'streamMarkers.time', 'Time')}</div>
                                        }
                                        {shouldDisplay(entry.gameSettings, 'streamMarkers.description') &&
                                            <div className="header label">{getLabel(entry.gameSettings, 'streamMarkers.description', 'Description')}</div>
                                        }
                                    </div>
                                    {entry.streamMarkers.map((marker, i) => {
                                        const altBgClass = i % 2 === 0 ? ' alt-bg' : '';
                                        const tData = convertSecondsToHMS(marker.time, true);
                                        return (
                                            <div className={"grid stream-markers" + altBgClass} key={'m_' + i}>
                                                {shouldDisplay(entry.gameSettings, 'streamMarkers.twitchHandle') &&
                                                    <div className="data"><a href={'https://www.twitch.tv/' + marker.twitchHandle} target="_blank">{marker.twitchHandle}</a></div>
                                                }
                                                {shouldDisplay(entry.gameSettings, 'streamMarkers.channel') &&
                                                    <div className="data"><a href={'https://www.twitch.tv/' + marker.channel} target="_blank">{marker.channel}</a></div>
                                                }
                                                {shouldDisplay(entry.gameSettings, 'streamMarkers.time') &&
                                                    <div className="data">{`${tData.h} hrs ${tData.m} mins ${tData.s} secs (${marker.time} seconds)`}</div>
                                                }
                                                {shouldDisplay(entry.gameSettings, 'streamMarkers.description') &&
                                                    <div className="data">{marker.description}</div>
                                                }
                                            </div>
                                        );
                                    })}
                                    {entry.streamMarkers.length === 0 &&
                                        <div className="grid stream-markers">
                                            <div className="data" colSpan="4">No Stream Markers Set</div>
                                        </div>
                                    }
                                </div>
                            </CollapsibleHeadedSection>
                        }
                        {shouldDisplay(entry.gameSettings, 'losers') &&
                            <CollapsibleHeadedSection
                                title={"Losers (" + entry.losers.length + ")"}
                            >
                                {getUserEntriesJsxBlock(entry.losers, timezoneData, [], null, null, null, entry.gameSettings)}
                            </CollapsibleHeadedSection>
                        }
                    </div>
                </CollapsibleHeadedSection>
            </div>
        );
    }

    const ActivationGroupsList = ({ entries, timezoneData, keyPrefix }) => {
        const groupedEntries = groupActivationsByDate(entries, timezoneData);
        const [activeActivationId, setActiveActivationId] = useState(null);

        const handleActivationToggle = (activationId, isExpanded) => {
            setActiveActivationId((oldVal) => {
                if (isExpanded) {
                    return activationId;
                }
                return oldVal === activationId ? null : oldVal;
            });
        };

        return groupedEntries.map((group, groupIndex) => {
            const activationIds = group.entries.map((entry, entryIndex, arr) => {
                return getActivationEntryId(entry, keyPrefix, (arr.length - entryIndex) - 1);
            });
            return (
            <div className="activation-date-group" key={`${keyPrefix}_${group.key}_${groupIndex}`}>
                <div className="activation-date-group-header">
                    <div className="activation-date-group-line" />
                    <div className="activation-date-group-label">{group.label}</div>
                    <div className="activation-date-group-line" />
                </div>
                <div className="activation-date-group-list fl-column">
                    {group.entries.map((entry, entryIndex, arr) => {
                        const activationId = getActivationEntryId(entry, keyPrefix, (arr.length - entryIndex) - 1);
                        return getArcadeDataEntryJsxBlock(entry, (arr.length - entryIndex) - 1, timezoneData, {
                            id: activationId,
                            open: activeActivationId === activationId,
                            onCollapseToggle: handleActivationToggle,
                        });
                    })}
                </div>
            </div>
        )});
    };

    const localTimezoneData = parseTimezone(getDefaultTimezone());
    return (
        <PopUpPanel closePanelFunc={props.closePanelFunc} className="arcade-data-popup" wide={true}>
                <div className="arcade-data-popup-header">
                    <div>
                        <h2>Arcade Data for {campaignContext.campaignData.campaignName}</h2>
                        <p className="arcade-data-popup-subtitle">Review captured live data by stream and feature, then drill into each activation.</p>
                    </div>
                </div>
                <div className="arcade-data-popup-content">
                <div className="arcade-data-toolbar">
                    <div className="arcade-data-toolbar-fields">
                        <div className="arcade-data-filter-copy">
                            <div className="form-field-content label">Filter By Feature / Disruptor</div>
                            <div className="arcade-data-filter-subtitle">Select one or more keys to filter the captured results instantly.</div>
                        </div>
                        <div className="arcade-data-filter-chips">
                            {featureFilterList.map((feature) => (
                                <button
                                    type="button"
                                    key={feature.value}
                                    className={`arcade-data-filter-chip${selectedFeatureFilters.includes(String(feature.value)) ? ' active' : ''}`}
                                    onClick={() => toggleFeatureFilter(feature.value)}
                                >
                                    <span className="arcade-data-filter-chip-icon"><LottieIcon varKey={feature.iconKey} size={18} /></span>
                                    <span>{feature.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="arcade-data-toolbar-actions">
                        {selectedFeatureFilters.length > 0 && (
                            <div className="standard-button tight secondary" onClick={clearFeatureFilters}>Clear Filters</div>
                        )}
                        <div className={`standard-button tight button-${busy ? 'in' : ''}active`} onClick={busy ? null : fetchData}>{`${busy ? 'Refreshing...' : 'Refresh Data'}`}</div>
                    </div>
                </div>
                <div className="arcade-data-scrolling-panel fl-column">
                    {streams_ref.current.map((stream, i) => {
                        if (arcadeDataFetched[stream._id]) {
                            const timezoneData = parseTimezone(stream.timeZone);
                            const filteredEntries = filteredEntriesForStream(stream._id);
                            if ((props.streamId && props.streamId !== stream._id) || (!filteredEntries.length && selectedFeatureFilters.length > 0)) {
                                return null;
                            }
                            return (
                                <OneStreamOverview 
                                    stream={stream} 
                                    timezoneData={timezoneData}
                                    useCustomContentChildren={true}
                                    key={'astream_' + i}
                                >
                                    <>
                                        {!arcadeDataFetched[stream._id] &&
                                            <>
                                                <div className="underlined-panel-heading">                                 
                                                    <h4>Fetching Data...</h4>
                                                </div>
                                            </>
                                        }
                                        {arcadeDataFetched[stream._id] && (!arcadeData[stream._id] || arcadeData[stream._id].length === 0) &&
                                            <div className="underlined-panel-heading">
                                                <h4>No Data To Display</h4>
                                            </div>
                                        }
                                        {arcadeDataFetched[stream._id] && filteredEntries && filteredEntries.length > 0 &&
                                            <>
                                                {/* <div className="underlined-panel-heading">
                                                    <h4>Data From Stream</h4>
                                                </div> */}
                                                <CollapsibleHeadedSection
                                                    title={`Data From Stream (${filteredEntries.length} Activation${filteredEntries.length === 1 ? '' : 's'})`}
                                                >
                                                    <div className="fl-column activations-list">
                                                        <ActivationGroupsList entries={filteredEntries} timezoneData={timezoneData} keyPrefix={`stream_${stream._id}`} />
                                                    </div>
                                                </CollapsibleHeadedSection>
                                            </>
                                        } 
                                        {arcadeDataFetched[stream._id] && selectedFeatureFilters.length > 0 && filteredEntries.length === 0 && arcadeData[stream._id] && arcadeData[stream._id].length > 0 &&
                                            <div className="underlined-panel-heading">
                                                <h4>No matching filtered data for this stream</h4>
                                            </div>
                                        }
                                    </>
                                </OneStreamOverview>
                            );
                        } else {
                            return null;
                        }
                    })}
                    {arcadeDataFetched['preview_test'] && (selectedFeatureFilters.length === 0 || (arcadeData['preview_test'] || []).some((entry) => selectedFeatureFilters.includes(String(entry.featureID)))) &&
                        <div className="arcade-data-box no-gap">
                            <div className="underlined-panel-heading">
                                <h4>Test Data From Previews</h4>
                            </div>
                            <div className="fl-column">
                                <ActivationGroupsList
                                    entries={(arcadeData['preview_test'] || []).filter((entry) => selectedFeatureFilters.length === 0 || selectedFeatureFilters.includes(String(entry.featureID)))}
                                    timezoneData={localTimezoneData}
                                    keyPrefix="preview_test"
                                />
                            </div>
                        </div>
                    }
                </div>
                </div>
        </PopUpPanel>
    )
}

export default ArcadeDataPopup;
